import React, { Suspense, lazy, useEffect } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useMobilePerformance } from '../hooks/useMobilePerformance';
import SectionOptimizer from '../components/common/SectionOptimizer';

const ChatMockup = lazy(() => import('../components/ChatMockup'));
const DashboardSnippet = lazy(() => import('../components/DashboardSnippet'));
const ROIComparison = lazy(() => import('../components/ROIComparison'));
const TrustBar = lazy(() => import('../components/TrustBar'));

const Home = () => {
  useEffect(() => {
    let link = document.querySelector("link[rel='canonical']");
    if (link) { link.setAttribute('href', 'https://getdmflow.com/'); }
  }, []);

  const { isMobile } = useMobilePerformance();

  const industries = [
    { name: 'Roofing', path: '/industries/roofing-ai-automation' },
    { name: 'HVAC', path: '/industries/hvac-ai-automation' },
    { name: 'Dentistry', path: '/industries/dentist-ai-automation' },
    { name: 'Real Estate', path: '/industries/real-estate-ai-automation' },
    { name: 'Med Spas', path: '/industries/med-spa-ai-automation' },
    { name: 'Solar', path: '/industries/solar-ai-automation' },
    { name: 'Automotive', path: '/industries/automotive-ai-automation' },
    { name: 'Law Firms', path: '/industries/law-firm-ai-automation' },
    { name: 'Agencies', path: '/features' },
    { name: 'Service Businesses', path: '/industries' }
  ];

  return (
    <div className="bg-brand-dark min-h-screen text-white overflow-x-hidden selection:bg-brand-cyan/30">
      
      {/* 1. HERO SECTION - UPDATED MESSAGING */}
      <section className="relative pt-24 pb-20 lg:pt-52 lg:pb-40 flex items-center min-h-[90vh] lg:min-h-screen">
        <div className="absolute inset-0 z-0 pointer-events-none overflow-hidden">
          <div className={`absolute top-[-5%] left-1/2 -translate-x-1/2 ${isMobile ? 'w-[600px] h-[400px] blur-[80px]' : 'w-[1400px] h-[800px] blur-[180px]'} bg-brand-blue/10 rounded-full`}></div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <motion.div initial={{ opacity: 0, y: isMobile ? 10 : 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: "easeOut" }}>
              <div className="inline-flex items-center gap-3 px-4 py-2 rounded-full border border-brand-cyan/20 bg-brand-cyan/5 text-brand-cyan text-[10px] font-black uppercase tracking-[0.25em] mb-8 shadow-[0_0_30px_rgba(34,211,238,0.15)]">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-brand-cyan opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-brand-cyan"></span>
                </span>
                AI Sales Automation Platform
              </div>
              <h1 className="text-5xl sm:text-7xl lg:text-[85px] font-black tracking-tight leading-[0.95] mb-8 lg:mb-10 uppercase">
                AI Sales Automation <br />
                <span className="text-gradient">That Responds, Qualifies & Books.</span>
              </h1>
              <p className="text-lg lg:text-2xl text-brand-gray mb-10 lg:mb-12 max-w-xl leading-relaxed font-medium">
                GetDMFlow helps businesses convert more leads through AI-powered lead response, customer communication, appointment booking, CRM automation, and sales funnel automation across multiple channels.
              </p>
              <div className="flex flex-col sm:flex-row gap-5 lg:gap-6">
                <Link to="/pricing" className="px-10 py-5 lg:px-12 lg:py-6 rounded-full bg-gradient-to-r from-brand-blue to-brand-cyan text-white font-black text-lg lg:text-xl hover:shadow-[0_0_60px_rgba(18,115,234,0.6)] hover:scale-[1.03] transition-all duration-500 flex items-center justify-center gap-4 group">
                  Start Free Trial
                </Link>
                <Link to="/demo" className="px-10 py-5 lg:px-12 lg:py-6 rounded-full glass-panel text-white font-black text-lg lg:text-xl hover:bg-white/10 transition-all duration-500 flex items-center justify-center">
                  Book Demo
                </Link>
              </div>
              <Suspense fallback={<div className="h-20" />}>
                <TrustBar />
              </Suspense>
            </motion.div>
            <div className="relative mt-12 lg:mt-0">
              <Suspense fallback={<div className="aspect-[9/16] bg-white/5 rounded-[3rem] animate-pulse" />}>
                <div className="relative z-20 scale-90 sm:scale-100 origin-center lg:origin-right">
                  <ChatMockup />
                </div>
              </Suspense>
            </div>
          </div>
        </div>
      </section>

      {/* 2. INDUSTRY TRUST SECTION */}
      <section className="py-24 border-y border-white/5 bg-white/[0.01]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionOptimizer className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-black text-white uppercase tracking-tighter mb-4">Trusted Across Multiple Industries</h2>
            <p className="text-brand-gray font-medium uppercase tracking-[0.2em] text-[10px]">Specialized AI Infrastructure For Your Vertical</p>
          </SectionOptimizer>
          <div className="flex flex-wrap justify-center gap-4">
            {industries.map((ind, i) => (
              <SectionOptimizer key={i} delay={i * 0.05}>
                <Link to={ind.path} className="px-6 py-4 rounded-2xl glass-panel border-white/5 hover:border-brand-cyan/30 text-[11px] font-black text-white uppercase tracking-widest transition-all hover:bg-white/5">
                  {ind.name}
                </Link>
              </SectionOptimizer>
            ))}
          </div>
        </div>
      </section>

      {/* 3. HOW IT WORKS SECTION */}
      <section className="py-32 bg-black/20">
        <div className="max-w-7xl mx-auto px-4">
          <SectionOptimizer className="text-center mb-24">
            <h2 className="text-4xl md:text-7xl font-black text-white uppercase tracking-tighter">How It Works</h2>
            <p className="text-brand-gray text-lg mt-4 font-medium">Three steps to automated revenue.</p>
          </SectionOptimizer>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { step: "01", title: "Connect Your Channels", desc: "Sync Instagram, Facebook, WhatsApp and Web Chat instantly.", icon: FiIcons.FiLink },
              { step: "02", title: "Train Your AI", desc: "Upload FAQs, services, offers and business information.", icon: FiIcons.FiCpu },
              { step: "03", title: "Convert More Leads", desc: "AI responds, qualifies, follows up and books appointments automatically.", icon: FiIcons.FiZap }
            ].map((item, i) => (
              <SectionOptimizer key={i} delay={i * 0.1} className="glass-panel p-10 rounded-[3rem] border-white/5 flex flex-col items-center text-center">
                <div className="w-16 h-16 rounded-2xl bg-brand-cyan/10 flex items-center justify-center text-brand-cyan mb-8 border border-brand-cyan/20">
                  <SafeIcon icon={item.icon} className="w-8 h-8" />
                </div>
                <h3 className="text-2xl font-black text-white mb-4 uppercase tracking-tight">{item.title}</h3>
                <p className="text-brand-gray font-medium leading-relaxed">{item.desc}</p>
              </SectionOptimizer>
            ))}
          </div>
        </div>
      </section>

      {/* 4. RESULTS SECTION */}
      <section className="py-32 border-y border-white/5 bg-white/[0.01]">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-20 items-center">
            <SectionOptimizer>
              <h2 className="text-4xl md:text-7xl font-black text-white uppercase tracking-tighter mb-10 leading-[1.1]">
                What Businesses <br /> <span className="text-gradient">Achieve With Us.</span>
              </h2>
              <div className="space-y-6">
                {[
                  "Respond to leads in seconds",
                  "Reduce missed opportunities",
                  "Book more appointments",
                  "Automate follow-up",
                  "Save valuable administrative time",
                  "Improve conversion efficiency"
                ].map((result, i) => (
                  <div key={i} className="flex items-center gap-4 text-white font-bold uppercase tracking-widest text-xs">
                    <div className="w-6 h-6 rounded-full bg-brand-cyan/20 flex items-center justify-center">
                      <SafeIcon icon={FiIcons.FiCheck} className="text-brand-cyan w-3 h-3" />
                    </div>
                    {result}
                  </div>
                ))}
              </div>
            </SectionOptimizer>
            <SectionOptimizer delay={0.2}>
              <DashboardSnippet />
            </SectionOptimizer>
          </div>
        </div>
      </section>

      {/* 5. DEMO VIDEO PLACEHOLDER */}
      <section className="py-40 relative">
        <div className="max-w-6xl mx-auto px-4">
          <SectionOptimizer className="text-center mb-16">
            <h2 className="text-4xl md:text-6xl font-black text-white uppercase tracking-tighter">See GetDMFlow In Action</h2>
          </SectionOptimizer>
          <SectionOptimizer delay={0.2}>
            <div className="glass-panel aspect-video rounded-[3rem] border-white/10 bg-black/40 flex items-center justify-center group cursor-pointer relative overflow-hidden">
               <div className="absolute inset-0 bg-gradient-to-br from-brand-blue/20 to-brand-cyan/20 opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>
               <div className="w-24 h-24 rounded-full bg-white text-brand-dark flex items-center justify-center transition-transform duration-500 group-hover:scale-110 z-10">
                 <SafeIcon icon={FiIcons.FiPlay} className="w-10 h-10 ml-1" />
               </div>
               <p className="absolute bottom-10 text-[10px] font-black text-brand-gray uppercase tracking-[0.4em] z-10">Infrastructure Walkthrough (Coming Soon)</p>
            </div>
          </SectionOptimizer>
        </div>
      </section>

      {/* 6. ANNUAL BILLING TEASER */}
      <section className="py-24 bg-brand-blue/5 border-y border-white/5">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid lg:grid-cols-4 gap-6 items-center">
            <SectionOptimizer className="lg:col-span-1">
              <h3 className="text-2xl font-black text-white uppercase tracking-tight mb-2 leading-none">Save 2 Months</h3>
              <p className="text-[10px] text-brand-cyan font-black uppercase tracking-widest mb-4">With Annual Billing</p>
              <Link to="/pricing" className="text-xs font-black text-white underline underline-offset-8 uppercase tracking-widest hover:text-brand-cyan transition-colors">View All Plans</Link>
            </SectionOptimizer>
            {[
              { tier: "Starter", price: "CAD $970/year" },
              { tier: "Growth", price: "CAD $2,970/year" },
              { tier: "Pro", price: "CAD $4,970/year" }
            ].map((p, i) => (
              <SectionOptimizer key={i} delay={i * 0.1} className="glass-panel p-8 rounded-3xl border-white/5 bg-white/[0.02] text-center">
                <p className="text-[10px] font-bold text-brand-gray uppercase tracking-widest mb-2">{p.tier}</p>
                <p className="text-2xl font-black text-white tracking-tighter">{p.price}</p>
              </SectionOptimizer>
            ))}
          </div>
        </div>
      </section>

      {/* 7. CREDIBILITY BLOCK */}
      <section className="py-20 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-4">
          <SectionOptimizer className="flex flex-col md:flex-row items-center justify-between gap-10 p-10 glass-panel rounded-[2.5rem] border-white/5">
             <div className="flex items-center gap-6">
                <div className="w-16 h-16 rounded-2xl bg-brand-blue/10 flex items-center justify-center">
                   <SafeIcon icon={FiIcons.FiShield} className="text-brand-cyan w-8 h-8" />
                </div>
                <div>
                   <h4 className="text-xl font-black text-white uppercase tracking-tight">Built in Canada</h4>
                   <p className="text-brand-gray text-sm font-medium">Headquartered in Calgary, Alberta.</p>
                </div>
             </div>
             <div className="text-center md:text-right">
                <p className="text-[10px] font-black text-brand-gray uppercase tracking-widest mb-2">A Product Of</p>
                <p className="text-lg font-black text-white uppercase tracking-tighter">Silahub Technologies Inc.</p>
             </div>
          </SectionOptimizer>
        </div>
      </section>

      {/* ROI Comparison - Kept for final conversion lift */}
      <section className="py-32 relative">
        <div className="max-w-7xl mx-auto px-4 relative z-10">
          <SectionOptimizer className="text-center mb-24">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-red-500/10 border border-red-500/20 text-red-500 text-[10px] font-black uppercase tracking-[0.2em] mb-8"> Critical Sales Insight </div>
            <h2 className="text-4xl lg:text-8xl font-black text-white mb-10 leading-tight uppercase tracking-tighter uppercase"> 78% of customers <br/> <span className="text-gradient">buy from the first reply.</span> </h2>
          </SectionOptimizer>
          <SectionOptimizer delay={0.2}>
            <Suspense fallback={<div className="h-96 bg-white/5 rounded-[3rem] animate-pulse" />}>
              <ROIComparison />
            </Suspense>
          </SectionOptimizer>
        </div>
      </section>
    </div>
  );
};

export default Home;